import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Package, ShoppingCart, Settings as SettingsIcon, Plus, Pencil, Trash2, X, Grid3x3, FileText, Palette, Paintbrush2, Megaphone, Users } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import AdminCategories from "./admin/AdminCategories";
import AdminPages from "./admin/AdminPages";
import AdminAppearance from "./admin/AdminAppearance";
import AdminStoreInfo from "./admin/AdminStoreInfo";
import AdminTheme from "./admin/AdminTheme";
import AdminPromotions from "./admin/AdminPromotions";
import AdminSubscribers from "./admin/AdminSubscribers";
import AdminProductForm from "./admin/AdminProductForm";
import AdminUsers from "./admin/AdminUsers";

const EMPTY = { name: "", description: "", price: "", sale_price: "", category: "", subcategory: "", image_url: "", images: [], badge: "", unit: "", in_stock: true, stock: 0, variants: [] };
const STATUSES = ["pending", "processing", "shipped", "delivered", "cancelled"];

export default function Admin() {
  const { user, ready } = useAuth();
  const { settings: ctxSettings, currency, labelFor, refresh: refreshSite } = useSettings();
  const [tab, setTab] = useState("orders");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [allCats, setAllCats] = useState([]);
  const [siteSettings, setSiteSettings] = useState(ctxSettings);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const loadAll = () => {
    api.get("/products?include_hidden=true").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/categories?all=true").then((r) => setAllCats(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "manager")) loadAll();
  }, [user]);

  useEffect(() => {
    setSiteSettings(ctxSettings);
  }, [ctxSettings]);

  if (!ready) return <div className="py-24 text-center text-[#5C665D]">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin" && user.role !== "manager") return <Navigate to="/" replace />;
  const isManager = user.role === "manager";

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, category: allCats[0]?.key || "" });
    setShowForm(true);
  };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      ...p,
      price: String(p.price),
      sale_price: p.sale_price ?? "",
      images: p.images && p.images.length ? p.images : (p.image_url ? [p.image_url] : []),
      variants: p.variants || [],
    });
    setShowForm(true);
  };

  const num = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));

  const saveProduct = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      sale_price: num(form.sale_price),
      stock: parseInt(form.stock) || 0,
      variants: (form.variants || []).map((v) => ({
        ...v,
        price: num(v.price),
        sale_price: num(v.sale_price),
        stock: parseInt(v.stock) || 0,
        reserved: v.reserved || 0,
        sold: v.sold || 0,
      })),
    };
    try {
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setShowForm(false);
      loadAll();
    } catch {
      toast.error("Could not save product");
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    loadAll();
  };

  const changeStatus = async (id, status) => {
    await api.put(`/orders/${id}/status?status=${status}`);
    toast.success("Order updated");
    loadAll();
  };

  const setField = (k, v) => setSiteSettings((s) => ({ ...s, [k]: v }));

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await api.put("/settings", siteSettings);
      toast.success("Settings saved");
      refreshSite();
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const selectedCat = allCats.find((c) => c.key === form.category);
  const hiddenKeys = allCats.filter((c) => !c.visible).map((c) => c.key);

  const allTabs = [
    { key: "orders", label: "Orders", icon: ShoppingCart },
    { key: "products", label: "Products", icon: Package },
    { key: "categories", label: "Categories", icon: Grid3x3, adminOnly: true },
    { key: "promotions", label: "Promotions", icon: Megaphone, adminOnly: true },
    { key: "pages", label: "Pages", icon: FileText, adminOnly: true },
    { key: "appearance", label: "Appearance", icon: Palette, adminOnly: true },
    { key: "theme", label: "Theme", icon: Paintbrush2, adminOnly: true },
    { key: "subscribers", label: "Subscribers", icon: Users, adminOnly: true },
    { key: "users", label: "Users", icon: Users, adminOnly: true },
    { key: "storeinfo", label: "Store Info", icon: SettingsIcon, adminOnly: true },
  ];
  const tabs = allTabs.filter((t) => !t.adminOnly || !isManager);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A231C] font-display">Admin Dashboard</h1>
      <p className="text-[#5C665D] mt-2">Manage your entire store — products, orders, categories, pages, look & feel.</p>

      {/* stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
        <div className="bg-white border border-[#E8E4D9] rounded-xl p-5">
          <p className="text-sm text-[#5C665D]">Total Orders</p>
          <p className="text-2xl font-display font-bold text-[#1A231C] mt-1">{orders.length}</p>
        </div>
        <div className="bg-white border border-[#E8E4D9] rounded-xl p-5">
          <p className="text-sm text-[#5C665D]">Products</p>
          <p className="text-2xl font-display font-bold text-[#1A231C] mt-1">{products.length}</p>
        </div>
        <div className="bg-white border border-[#E8E4D9] rounded-xl p-5">
          <p className="text-sm text-[#5C665D]">Categories</p>
          <p className="text-2xl font-display font-bold text-[#1A231C] mt-1">{allCats.length}</p>
        </div>
        <div className="bg-white border border-[#E8E4D9] rounded-xl p-5">
          <p className="text-sm text-[#5C665D]">Pending</p>
          <p className="text-2xl font-display font-bold text-[var(--brand-accent)] mt-1">
            {orders.filter((o) => o.status === "pending").length}
          </p>
        </div>
      </div>

      {/* tabs */}
      <div className="flex gap-1 mt-8 border-b border-[#E8E4D9] overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              tab === t.key ? "border-[var(--brand-primary)] text-[var(--brand-primary)]" : "border-transparent text-[#5C665D] hover:text-[#1A231C]"
            }`}
          >
            <t.icon className="w-4 h-4" strokeWidth={1.5} /> {t.label}
          </button>
        ))}
      </div>

      {/* Orders */}
      {tab === "orders" && (
        <div className="mt-6 space-y-4">
          {orders.length === 0 && <p className="text-[#5C665D]">No orders yet.</p>}
          {orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="bg-white border border-[#E8E4D9] rounded-xl p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <p className="font-display font-semibold text-[#1A231C]">{o.customer_name} · {currency}{o.total}</p>
                  <p className="text-sm text-[#5C665D]">{o.phone} · {new Date(o.created_at).toLocaleString()}</p>
                  <p className="text-sm text-[#5C665D] mt-1">{o.address}</p>
                </div>
                <select data-testid={`admin-order-status-${o.id}`} value={o.status} onChange={(e) => changeStatus(o.id, e.target.value)} className="bg-white border border-[#E8E4D9] rounded-lg px-3 py-2 text-sm capitalize focus:outline-none focus:border-[var(--brand-primary)]">
                  {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              </div>
              <div className="mt-3 pt-3 border-t border-[#E8E4D9] text-sm text-[#5C665D]">
                {o.items.map((it) => `${it.name} × ${it.qty}`).join(", ")}
                {o.note && <p className="mt-1 italic">Note: {o.note}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Products */}
      {tab === "products" && (
        <div className="mt-6">
          <button data-testid="admin-add-product" onClick={openNew} className="inline-flex items-center gap-2 bg-[var(--brand-primary)] text-white px-4 py-2.5 rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors mb-4">
            <Plus className="w-4 h-4" /> Add Product
          </button>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.id} className={`bg-white border border-[#E8E4D9] rounded-xl p-4 flex gap-3 ${hiddenKeys.includes(p.category) ? "opacity-60" : ""}`}>
                <img src={p.image_url} alt={p.name} className="w-16 h-16 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-display font-medium text-[#1A231C] truncate">{p.name}</p>
                  <p className="text-sm text-[#5C665D]">{labelFor(p.subcategory || p.category)} · {currency}{p.price}{p.sale_price ? ` → ${currency}${p.sale_price}` : ""}</p>
                  {(() => {
                    const vs = p.variants || [];
                    const avail = vs.length ? vs.reduce((s, v) => s + (v.stock || 0), 0) : (p.stock || 0);
                    const res = vs.length ? vs.reduce((s, v) => s + (v.reserved || 0), 0) : (p.reserved || 0);
                    const sold = vs.length ? vs.reduce((s, v) => s + (v.sold || 0), 0) : (p.sold || 0);
                    return (
                      <p className="text-xs mt-0.5" data-testid={`admin-inventory-${p.id}`}>
                        <span className="text-emerald-700">Avail {avail}</span> · <span className="text-amber-700">Reserved {res}</span> · <span className="text-[#5C665D]">Sold {sold}</span>
                        {vs.length ? ` · ${vs.length} variants` : ""}
                      </p>
                    );
                  })()}
                  <div className="flex gap-2 mt-2">
                    <button data-testid={`admin-edit-product-${p.id}`} onClick={() => openEdit(p)} className="text-[var(--brand-primary)] p-1.5 rounded hover:bg-[#F4EFE6]"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`admin-delete-product-${p.id}`} onClick={() => deleteProduct(p.id)} className="text-[var(--brand-accent)] p-1.5 rounded hover:bg-[#F4EFE6]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "categories" && <AdminCategories onChanged={() => { loadAll(); refreshSite(); }} />}
      {tab === "promotions" && <AdminPromotions onChanged={refreshSite} />}
      {tab === "pages" && <AdminPages onChanged={refreshSite} />}
      {tab === "appearance" && <AdminAppearance settings={siteSettings} setField={setField} onSave={saveSettings} saving={savingSettings} />}
      {tab === "theme" && <AdminTheme settings={siteSettings} setField={setField} onSave={saveSettings} saving={savingSettings} />}
      {tab === "subscribers" && <AdminSubscribers />}
      {tab === "users" && <AdminUsers />}
      {tab === "storeinfo" && <AdminStoreInfo settings={siteSettings} setField={setField} onSave={saveSettings} saving={savingSettings} />}

      {/* Product form modal */}
      {showForm && (
        <AdminProductForm
          form={form}
          setForm={setForm}
          editing={editing}
          allCats={allCats}
          onSave={saveProduct}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}
